import type { ColumnConfig, SalesHierarchyNode } from "../types"
import { summarizeForecastHierarchyTotals } from "./forecast_table_pipeline"
import { read_trade_spend } from "./gross_to_net"

/**
 * The grid's current revenue for every node that carries a trade spend plan, keyed by node id:
 * what `build_gross_to_net` needs to follow the scenario. Nodes are found in `data` (where the
 * plan lives) and summed from `processed` (where Baseline / Weighted / Full Upside and unsaved
 * edits have been applied), matched by id. Empty when no node carries a plan.
 */
export function live_customer_revenue(
  data: SalesHierarchyNode[],
  processed: SalesHierarchyNode[],
  period_keys: string[],
  column_config: ColumnConfig
): Map<string, number> {
  const wanted = new Set<string>()
  function collect(node: SalesHierarchyNode): void {
    if (read_trade_spend(node) && node.id) wanted.add(String(node.id))
    for (const child of (node as { children?: SalesHierarchyNode[] }).children ?? []) collect(child)
  }
  for (const root of data) collect(root)
  const out = new Map<string, number>()
  if (wanted.size === 0) return out
  function visit(node: SalesHierarchyNode): void {
    const id = node.id ? String(node.id) : ""
    if (wanted.has(id) && !out.has(id)) {
      const totals = summarizeForecastHierarchyTotals([node], period_keys, column_config)
      out.set(id, totals.actual_volume_dollars + totals.forecast_volume_dollars)
    }
    for (const child of (node as { children?: SalesHierarchyNode[] }).children ?? []) visit(child)
  }
  for (const root of processed) visit(root)
  return out
}
